// src/components/SignUpForm.tsx
// Componente de formulario de registro

import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useTenant } from '../../contexts/TenantContext';
import { apiService } from '../../services/api';
import type { SignUpData } from '../../types';

interface SignUpFormProps {
  onGoogleLogin: () => void;
  onOffice365Login: () => void;
  onSuccess: (name: string, surname: string) => void;
}

export const SignUpForm: React.FC<SignUpFormProps> = ({
  onGoogleLogin,
  onOffice365Login,
  onSuccess,
}) =>
{
  const { tenant } = useTenant();
  const [signUpData, setSignUpData] = useState<SignUpData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [signUpError, setSignUpError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSignUpChange = (e: React.ChangeEvent<HTMLInputElement>) =>
  {
    setSignUpData({ ...signUpData, [e.target.name]: e.target.value });
    setSignUpError('');
  };

  const handleSignUp = async () =>
  {
    if (
      !signUpData.firstName ||
      !signUpData.lastName ||
      !signUpData.email ||
      !signUpData.password ||
      !signUpData.confirmPassword
    )
    {
      setSignUpError('Please fill in all fields');
      return;
    }

    if (signUpData.password !== signUpData.confirmPassword)
    {
      setSignUpError('Passwords do not match');
      return;
    }

    if (signUpData.password.length < 6)
    {
      setSignUpError('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    setSignUpError('');

    try
    {
      await apiService.post('account/register', {
        tenancyName: tenant?.TenancyName || 'Default',
        name: signUpData.firstName,
        surname: signUpData.lastName,
        userName: signUpData.email,
        emailAddress: signUpData.email,
        password: signUpData.password,
      });

      onSuccess(signUpData.firstName, signUpData.lastName);
    }
    catch (err: any)
    {
      const message =
        err?.response?.data?.error?.message ||
        err?.message ||
        'Error creating account. Please try again.';
      setSignUpError(message);
    }
    finally
    {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) =>
  {
    if (e.key === 'Enter') handleSignUp();
  };

  return (
    <>
      <p className="text-center text-gray-600 text-sm mb-4">Sign up with:</p>

      {/* Social Login Buttons */}
      <div className="flex gap-3 mb-4">
        <button
          onClick={onGoogleLogin}
          className="flex-1 flex items-center justify-center gap-2 py-2 px-4 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          <span className="text-sm">Google</span>
        </button>

        <button
          onClick={onOffice365Login}
          className="flex-1 flex items-center justify-center gap-2 py-2 px-4 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#EA3E23" d="M22 9.5L12 4v7.5l10-2z" />
            <path fill="#EA3E23" d="M22 12l-10 2v7.5l10-5.5z" />
            <path fill="#C74018" d="M12 11.5V4L2 7v10l10 3v-7.5z" />
          </svg>
          <span className="text-sm">Office 365</span>
        </button>
      </div>

      <p className="text-center text-gray-600 text-sm mb-4">Or fill up the form:</p>

      {/* Sign Up Form */}
      <div>
        {signUpError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">
            {signUpError}
          </div>
        )}

        <div className="flex gap-3 mb-4">
          <input
            type="text"
            name="firstName"
            placeholder="First Name"
            value={signUpData.firstName}
            onChange={handleSignUpChange}
            onKeyPress={handleKeyPress}
            className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-[#62accb]"
          />
          <input
            type="text"
            name="lastName"
            placeholder="Last Name"
            value={signUpData.lastName}
            onChange={handleSignUpChange}
            onKeyPress={handleKeyPress}
            className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-[#62accb]"
          />
        </div>

        <div className="mb-4">
          <input
            type="email"
            name="email"
            placeholder="Username/E-Mail"
            value={signUpData.email}
            onChange={handleSignUpChange}
            onKeyPress={handleKeyPress}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-[#62accb]"
          />
        </div>

        <div className="mb-4">
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              placeholder="Password"
              value={signUpData.password}
              onChange={handleSignUpChange}
              onKeyPress={handleKeyPress}
              className="w-full border border-gray-300 rounded px-3 py-2 pr-10 text-sm outline-none focus:border-[#62accb]"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div className="mb-6">
          <div className="relative">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              placeholder="Confirm password"
              value={signUpData.confirmPassword}
              onChange={handleSignUpChange}
              onKeyPress={handleKeyPress}
              className="w-full border border-gray-300 rounded px-3 py-2 pr-10 text-sm outline-none focus:border-[#62accb]"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <button
          onClick={handleSignUp}
          disabled={isLoading}
          className="w-full bg-[#62accb] hover:bg-[#4a9bd1] text-white py-3 rounded font-medium transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Creating account...' : 'Sign Up'}
        </button>
      </div>
    </>
  );
};
